import { VesselPerformanceModelBase, VesselConfigData, VoyagePlan, PerfDataSetName, CurrentLeg, ParameterValue, ParameterType, RemainingDataToPilot, RemainingDataToBerth } from './vessel-performance-model';
import { PerformanceUtils} from '../utils/performance-utils';
import { PerformanceService, PerformanceData } from '../services/performance-service';
import { VoyageRoutingModel, VoyageRoutePointFeature, VoyageRouteLineFeature, VoyageRoute, RouteGeometry } from '../models/voyage-routing-model';
import { VesselPositionModel } from '../models/vessel-position-model';
import { Coordinate } from 'ol/coordinate';
import { CoordinatesUtils } from '../utils/coordinates-utils';
import { VesselPlanningModel } from './vessel-planning-model';
import { VoyageImoModel } from './voyage-imo-model';
import { VoyageImoEventNames } from './voyage-imo-model-impl';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
import { getNextWaypointIndex, getPilotPointIndeces, getRouteDistance } from '../utils/route-utils';

export interface PlanDataPoint
{
    sog?: number;
    stw?: number;
    ts: number;
    eta?: number|null;
    coords: Coordinate;
    mepower?: number;
    mepowerLower?: number;
    mepowerUpper?: number;
    merpm?: number;
    merpmLower?: number;
    merpmUpper?: number;
    waypointDetails: string;
    pilot: boolean;
    distance: number;
    waypointType: string;
    legType: "Rhumbline"|"GreatCircle";
    is_stoppage_point?: boolean;
    stoppage_end?: boolean;
    etd?: number|null;
    roll_angle?: number;
}

interface RouteData
{
    data: PlanDataPoint[];
    voyageRoute: VoyageRoute;
}

interface RemainingDataToWaypoint
{
    distance: number | null;
    timeToEta: number | null;
    avgSpeedToEta: number | null;
}

export class VesselPerformanceModelImpl extends VesselPerformanceModelBase {
    constructor()
    {
        super();
        let intervalID: number;        
        this._updateExecutingPerformanceData = this._updateExecutingPerformanceData.bind(this);
        this._updateExecutingPerformanceDataPromise = this._updateExecutingPerformanceData();
        VesselPlanningModel!.addEventListener('executing-plan-changed', () => this._updateExecutingPerformanceDataPromise = this._updateExecutingPerformanceData());
        intervalID = setInterval(() => this._updateExecutingPerformanceDataPromise = this._updateExecutingPerformanceData(), (15 * 60 * 1000)); // 15 minutes
        // logic executes when IMO gets updated - runs only for cloud
        VoyageImoModel!.addEventListener(VoyageImoEventNames.ImoUpdated, async () => {
            this._vesselConfiguration = await PerformanceService.fetchVesselConfigurationData(VoyageImoModel!.imo!);
            this._updateExecutingPerformanceDataPromise = this._updateExecutingPerformanceData();
            if (intervalID) {
                clearInterval(intervalID); // Stop the existing interval
            }
            intervalID = setInterval(() => this._updateExecutingPerformanceDataPromise = this._updateExecutingPerformanceData(), (15 * 60 * 1000)); // 15 minutes
        });
    }



    private _currentLegData: CurrentLeg|null = null;
    private _updateExecutingPerformanceDataPromise: Promise<void>;

    private _remainingDataToPilot: RemainingDataToPilot | null = null;
    private _remainingDataToBerth: RemainingDataToBerth | null = null;

    getRemainingDataToPilot(): RemainingDataToPilot | null {
        return this._remainingDataToPilot;
    }

    getRemainingDataToBerth(): RemainingDataToBerth | null {
        return this._remainingDataToBerth;
    }
    
    getCurrentLegData()
    {
        return this._currentLegData;
    }

    async getConfigurationData() {
        if (this._vesselConfiguration === null) {
            this._vesselConfiguration = await PerformanceService.fetchVesselConfigurationData(this._imoNo);
        }

        return this._vesselConfiguration;
    }

    async getLegData(date: Date, dataSetName: PerfDataSetName): Promise<CurrentLeg> {
        const KmToNM = 1 / 1.852; // Km -> Nautical miles

        const vesselConfiguration = await this.getConfigurationData() || {} as VesselConfigData;
        let legData: CurrentLeg = PerformanceUtils.ZeroCurrentLeg();

        if (this._perfData && dataSetName === "current") {
            if (this._perfData.mepower)
            {
                legData.MEPower.current = this._perfData.mepower[this._perfData.mepower.length - 1];
            }
            if (this._perfData.merpm)
            {
                legData.RPM.current = this._perfData.merpm[this._perfData.merpm.length - 1];
            }
            if (this._perfData.sog) {
                for (let i = this._perfData.sog.length - 1; i >= 0; i--) {
                    const v = this._perfData.sog[i];
                    if (v !== null && v !== undefined && v <= PerformanceUtils.MAX_AIS_SPEED_KNOTS) {
                        legData.SOG.current = v;
                        break;
                    }
                }
            }
            if (this._perfData.stw)
            {
                legData.STW.current = this._perfData.stw[this._perfData.stw.length - 1];
            }
        }

        legData.MEPower.max = vesselConfiguration!.maxPower;
        legData.RPM.max = vesselConfiguration!.maxRpm;
        legData.SOG.max = vesselConfiguration!.maxSpeed;

        if (this._data && this._data.has(dataSetName)) {
            const routeData = this._data.get(dataSetName);
            if (routeData) {
                const planData = routeData.data;

                let position: Coordinate = [];
                let currentWpIndex: number = -1;

                //active route
                if (dataSetName === "current")
                {
                    if (VesselPositionModel!.position && VesselPositionModel!.position.longitude && VesselPositionModel!.position.latitude)
                    {
                        position = [VesselPositionModel!.position.longitude, VesselPositionModel!.position.latitude];
                    
                        const routeWithInfoWPs = this._getRouteIncludingInfoWPs(routeData.voyageRoute.route_geometry);
                        const nextWpData = await VoyageRoutingModel!.getNextWaypointIndex(-1, position, routeWithInfoWPs);
                        if (nextWpData.result !== "failure")
                        {
                            const feature = routeWithInfoWPs[nextWpData.nextWPIndex];
    
                            // find the corresponding point in the plan data
                            const index = planData.findIndex(d => d.coords[0] === feature.geometry.coordinates[0] &&
                                                                  d.coords[1] === feature.geometry.coordinates[1]);
                            
                            currentWpIndex = Math.max(index-1, 0); // after getting next waypoint, simply move one back to get the current data
                        }    
                    }
                }
                else
                {
                    // routes from planning mode
                    const positionAndHeading = VoyageRoutingModel!.getPositionAndHeadingInTime(date.getTime(), routeData.voyageRoute);
                    
                    if (positionAndHeading !== null) {
                        position = positionAndHeading.position;
                        for (let i = 0; i < planData.length - 1; i++) {
                            if (new Date(planData[i].ts) <= new Date(date) && new Date(planData[i+1].ts) > new Date(date)) {
                                currentWpIndex = i;
                                break;
                            }
                        }
                    }
                }

                if (currentWpIndex !== -1)
                {
                    legData.MEPower.simulated = { value: planData[currentWpIndex].mepower! };
                    legData.MEPower.simulated.lower = planData[currentWpIndex].mepowerLower;
                    legData.MEPower.simulated.upper = planData[currentWpIndex].mepowerUpper;
                    legData.RPM.simulated =  { value: planData[currentWpIndex].merpm! };
                    legData.RPM.simulated.lower = planData[currentWpIndex].merpmLower;
                    legData.RPM.simulated.upper = planData[currentWpIndex].merpmUpper;
                    legData.SOG.simulated = { value: planData[currentWpIndex].sog! * 3.6 * KmToNM };
                    legData.STW.simulated = { value: planData[currentWpIndex].stw! * 3.6 * KmToNM };
                    legData.RollAngle.simulated = { value: planData[currentWpIndex].roll_angle! };

                    let nextWpIndex = -1;
                    // Skip data points until we hit the next leg
                    for (let i = currentWpIndex+1; i < planData.length; i++)
                    {
                        if (planData[i].waypointType !== "Informational")
                        {
                            nextWpIndex = i;
                            break;
                        }
                    }
                    if (nextWpIndex !== -1)
                    {
                        legData.MEPower.nextLeg = planData[nextWpIndex].mepower!;
                        legData.RPM.nextLeg = planData[nextWpIndex].merpm!;                        
                        legData.SOG.nextLeg = planData[nextWpIndex].sog! * 3.6 * KmToNM;
                        legData.STW.nextLeg = planData[nextWpIndex].stw! * 3.6 * KmToNM;
                        legData.RollAngle.nextLeg = planData[nextWpIndex].roll_angle || null;
                        const dists = CoordinatesUtils.calcDistance(position, planData[nextWpIndex].coords);
                        legData.info.distance = (planData[currentWpIndex].legType === "GreatCircle") ? dists.gc : dists.rl;

                        // find pilot and calculate distance
                        let pilotIndex = -1;
                        for (let index = nextWpIndex; index < planData.length; index++) {
                            if (planData[index].pilot) { 
                                pilotIndex = index; 
                                break; 
                            }
                        }

                        if (pilotIndex !== -1) {
                            let distanceToPilot = legData.info.distance;
                            for (let index = nextWpIndex; index < pilotIndex; index++) {
                                distanceToPilot += planData[index].distance * KmToNM;
                            }
                            legData.info.distanceToPilot = distanceToPilot;
                        }

                        let timeToNextWp;
                        if(legData.SOG.current !== null && legData.SOG.current! > 0) {
                            timeToNextWp = (legData.info.distance / legData.SOG.current!)*3600000;
                            legData.info.etaBasedOnCurrentSpeed = new Date(Date.now() + timeToNextWp);
                        }

                        if (pilotIndex !== -1) {
                            const pilotWp = planData[pilotIndex];
                            legData.info.timeToPilotTs = (new Date(pilotWp.ts).getTime() - Date.now());   
                            legData.info.pilotTimestamp = new Date(pilotWp.ts);

                            // plan pilot ETAs are always source of truth, so use them if available
                            const executingPlan = await VesselPlanningModel!.getExecutingPlan();
                            if (executingPlan && executingPlan.arrivalPilotEta > -1) {
                                legData.info.timeToPilotEta = (new Date(executingPlan.arrivalPilotEta).getTime() - Date.now());
                                legData.info.avgSpeedToPilotEta = legData.info.timeToPilotEta ? legData.info.distanceToPilot! / legData.info.timeToPilotEta * 3600000 : null;
                            }
                        }
                
                        legData.info.nextLegWaypointDetails = planData[nextWpIndex].waypointDetails; 
                    }
                }
            }
        }
        return legData;
    }

    async syncData(fromDate: Date, toDate: Date, plan: VoyagePlan) {
            this._perfData = await PerformanceService.fetchPerformanceData(this._imoNo, fromDate, toDate);

        this.dispatchEvent(new Event('papi-performance-updated'));

        this._data = new Map<string, RouteData>();

        if (plan && plan.routes.length >= 1 && plan.selectedRouteIndex >= 0)
        {
            const _planData = this.createDataSet(plan.routes[plan.selectedRouteIndex].route_geometry);
            this._data.set("current", { data: _planData, voyageRoute: plan.routes[plan.selectedRouteIndex]});
        }
        else
        {
            this._data.delete("current");
        }

        this.syncUiRoutesData();
    }

    syncUiRoutesData() {
        const uiRoutes = VoyageRoutingModel!.uiRoutes;    

        if (!this._data) {
            this._data = new Map<string, RouteData>();
        }

        for (let index = 0; index < uiRoutes.length; index++) {
            const routeSetName = "route"+(index+1);
            const route = uiRoutes[index].route;

            // if route_info is not present, it means the route has not been calculated
            // and thus there will be no performance data
            if (route && !route.route_info) {
                continue;
            }
            
            let dataSet = new Array<PlanDataPoint>();
            if (route && route.route_geometry) {
                dataSet = this.createDataSet(route.route_geometry);
            }

            this._data!.set(routeSetName, { data: dataSet, voyageRoute: route! });
        }

        this.dispatchEvent(new Event('performance-updated'));
    }

    createDataSet(route_geometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature>)
    {
        const dataset: PlanDataPoint[] = [];
        for (let i = 0; i < route_geometry.length; i++)
        {
            const feature = route_geometry[i];
            if (feature.geometry.type === "LineString")
            {
                if (feature.properties.informationalWaypoints)
                {
                    for (const point of feature.properties.informationalWaypoints)
                    {
                        this._addToDataSet(dataset, {
                            sog: point.properties.speed_over_ground,
                            stw: point.properties.speed_through_water,
                            ts: new Date(point.properties.timestamp).getTime(),
                            eta: new Date(point.properties.eta).getTime(),
                            coords: point.geometry.coordinates,
                            mepower: point.properties.propulsion_power,
                            mepowerLower: point.properties.propulsion_power_lower,
                            mepowerUpper: point.properties.propulsion_power_upper,
                            merpm: point.properties.main_engine_rpm,
                            merpmLower: point.properties.main_engine_rpm_lower,
                            merpmUpper: point.properties.main_engine_rpm_upper,
                            waypointDetails: point.properties.name || "",
                            pilot: point.properties.pilotPoint,
                            distance: point.properties.distance/1000, // convert to km
                            waypointType: point.properties.type,
                            legType: feature.properties.leg_type,
                            roll_angle: point.properties.roll_angle
                        });
                    }
                }
                else
                {
                    // Pull timestamp, name and type from the start point
                    const timestamp = (<VoyageRoutePointFeature>route_geometry[i-1]).properties.timestamp!;
                    const name = (<VoyageRoutePointFeature>route_geometry[i-1]).properties.name;
                    const waypointType = (<VoyageRoutePointFeature>route_geometry[i-1]).properties.type;

                    let pilotPoint = false;
                    if (i > 0 && route_geometry[i-1].geometry.type === "Point" && route_geometry[i-1].properties.pilotPoint === true) pilotPoint = true;

                    this._addToDataSet(dataset, {
                        sog: feature.properties.speed_over_ground,
                        stw: feature.properties.speed_through_water,
                        ts: new Date(timestamp).getTime(),
                        eta: feature.properties.eta ? new Date(feature.properties.eta).getTime() : null,
                        coords: feature.geometry.coordinates[0],
                        mepower: feature.properties.propulsion_power,
                        mepowerLower: feature.properties.propulsion_power_lower,
                        mepowerUpper: feature.properties.propulsion_power_upper,
                        merpm: feature.properties.main_engine_rpm,
                        merpmLower: feature.properties.main_engine_rpm_lower,
                        merpmUpper: feature.properties.main_engine_rpm_upper,
                        waypointDetails: name || "",
                        pilot: pilotPoint,
                        distance: feature.properties.distance,
                        waypointType: waypointType!,
                        legType: feature.properties.leg_type
                    });
                }
            }
        }

        //last waypoint
        const lastWaypoint = (<VoyageRoutePointFeature>route_geometry[route_geometry.length - 1]);
        dataset.push({
            sog: 0,
            stw: 0,
            ts: new Date((lastWaypoint).properties.timestamp!).getTime(), 
            eta: lastWaypoint.properties.eta ? new Date((lastWaypoint).properties.eta).getTime() : null,
            coords: (lastWaypoint).geometry.coordinates,
            mepower: 0,
            mepowerLower: 0,
            mepowerUpper: 0,
            merpm: 0,
            merpmLower: 0,
            merpmUpper: 0,
            waypointDetails: (lastWaypoint).properties.name || "",
            pilot: route_geometry[route_geometry.length - 1].properties.pilotPoint,
            distance: 0.0,
            waypointType: (lastWaypoint).properties.type!,
            legType: (<VoyageRoutePointFeature>route_geometry[route_geometry.length - 2]).properties.leg_type
        });
        return dataset;
    }

    extractOneParam(dataSet: PerfDataSetName, type: ParameterType, onlyPositiveValues: boolean = false, etaChange?: boolean, requestedPlanData?: PlanDataPoint[]): ParameterValue[]
    {
        if (!this._data) {
            return [];
        }

        if (!this._data.has(dataSet) && !etaChange) {
            return [];
        }
        
        const planData = etaChange ? requestedPlanData : this._data.get(dataSet)!.data;

        if (!planData || planData.length == 0) return [];

        const result: ParameterValue[] = [];

        for (const waypoint of planData)
        {
            const value: Partial<ParameterValue> = {
                timestamp: waypoint.ts,
                value: 0
            };

            switch (type)
            {
                case ParameterType.PropulsionPower:
                    // Already in kw
                    value.value = waypoint.mepower;
                    value.lower = waypoint.mepowerLower;
                    value.upper = waypoint.mepowerUpper;
                    break;

                case ParameterType.RPM:
                    value.value = waypoint.merpm;
                    value.lower = waypoint.merpmLower;
                    value.upper = waypoint.merpmUpper;
                    break;

                case ParameterType.SpeedOverGround:
                    // Convert from m/s to knots
                    if (waypoint.sog === undefined || waypoint.sog === null) {
                        continue;
                    }
                    value.value = waypoint.sog * (60 * 60) / 1852;
                    if (value.value > PerformanceUtils.MAX_AIS_SPEED_KNOTS) {
                        continue;
                    }
                    break;
                case ParameterType.SpeedThroughWater:
                    // Convert from m/s to knots
                    value.value = waypoint.stw!*(60*60)/1852;
                    break;
                case ParameterType.CurrentImpact:
                    // Calculating SOG - STW (impact of current and wind)
                    // Convert from m/s to knots
                    const sogKnots = waypoint.sog! * (60*60) / 1852;
                    const stwKnots = waypoint.stw! * (60*60) / 1852;
                    value.value = sogKnots - stwKnots;
                    break;
                case ParameterType.RollAngle:
                    value.value = waypoint.roll_angle;
                    break;
            }

            if (onlyPositiveValues && value.value !== undefined && value.value < 0)
            {
                value.value = 0;
            }

            value.value = Math.round(value.value! * 10) / 10;
            if (value.lower)
            {
                value.lower = Math.round(value.lower * 10) / 10;
            }
            if (value.upper)
            {
                value.upper = Math.round(value.upper * 10) / 10;
            }

            // Omit missing values
            if (!isNaN(value.value))
            {
                result.push(<ParameterValue>value);
            }
        }

        return result;
    }

    extractOnePAPIParam(type: ParameterType, onlyPositiveValues: boolean = false): ParameterValue[]
    {
        if (!this._perfData) return [];

        const ts = this._perfData.ts;
        let vals: (number|null)[]|null = null;

        switch (type)
        {
            case ParameterType.PropulsionPower:
                vals = this._perfData.mepower;
                break;

            case ParameterType.RPM:
                vals = this._perfData.merpm;
                break;
            
            case ParameterType.SpeedOverGround:
                vals = this._perfData.sog;
                break;
            case ParameterType.SpeedThroughWater:
                vals = this._perfData.stw;
                break;
            case ParameterType.CurrentImpact:
                if (!this._perfData.sog || !this._perfData.stw) return [];
                
                vals = this._perfData.sog.map((sog, i) => {
                    const stw = this._perfData!.stw![i];
                    if (sog === null || stw === null) return null;
                    return sog - stw;
                });
                break;
        }

        if (!ts || !vals || ts.length !== vals.length) return [];

        const res: ParameterValue[] = [];
        for (let i=0; i<ts.length; i++)
        {
            let value = vals[i];

            if (onlyPositiveValues && (value === null || value < 0)) value = 0;

            // Omit missing values
            if (value !== null)
            {
                if (type === ParameterType.SpeedOverGround && value > PerformanceUtils.MAX_AIS_SPEED_KNOTS) {
                    continue;
                }
                res.push({
                    timestamp: ts[i],
                    value: Math.round(value * 10) / 10
                });
            }
        }

        return res;
    }

    private _data: Map<string, RouteData>|null = null;
    private _perfData: PerformanceData|null = null;
    private _imoNo: number = (<any>window)["appenv"].vesselIMONumber;
    private _vesselConfiguration: VesselConfigData|null = null;

    // Creates a route geometry with LineStrings between each informational waypoint (for use with getNextWaypointIndex)
    private _getRouteIncludingInfoWPs(route: RouteGeometry): RouteGeometry
    {
        const result: RouteGeometry = [];

        for (let i = 0; i < route.length; i++)
        {
            const feature = route[i];
            const addPoints: VoyageRoutePointFeature[] = [];
            if (feature.geometry.type === "Point")
            {
                addPoints.push({
                    type: "Feature",
                    geometry: feature.geometry,
                    properties: {
                        isPassed: feature.properties.isPassed
                    }
                });
            }
            else if (feature.properties.informationalWaypoints)
            {
                // Look ahead to next waypoint for isPassed
                const isPassed = route[i+1].properties.isPassed;

                for (const infPoint of feature.properties.informationalWaypoints)
                {
                    addPoints.push({
                        type: "Feature",
                        geometry: infPoint.geometry,
                        properties: { isPassed }
                    });
                }
            }

            for (const addPoint of addPoints)
            {
                if (result.length > 0)
                {
                    const legType = feature.geometry.type === "Point" ? route[i-1].properties.leg_type : feature.properties.leg_type;
                    result.push({
                        type: "Feature",
                        geometry: {
                            type: "LineString",
                            coordinates: [ (<VoyageRoutePointFeature>result[result.length-1]).geometry.coordinates, addPoint.geometry.coordinates ]
                        },
                        properties: {
                            leg_type: legType
                        }
                    });
                }
                result.push(addPoint);
            }
        }

        return result;
    }

    private _addToDataSet(dataSet: PlanDataPoint[], dataPoint: PlanDataPoint)
    {
        // Correct for overlapping timestamps (this can happen after a re-calc when a vessel is further along than expected).
        while (dataSet.length > 0 && dataPoint.ts <= dataSet[dataSet.length - 1].ts) 
        {
            dataSet.pop();
        }
        dataSet.push(dataPoint);
    }

    private async updateRemainingDataToPilot() {
        const defaultResult = { distanceToPilot: null, timeToPilotEta: null, avgSpeedToPilotEta: null };
    
        const plan = await VesselPlanningModel?.getExecutingPlan();
        if (!plan) {
            console.log("No executing plan found");
            return defaultResult;
        }
    
        const { routes, selectedRouteIndex, arrivalPilotEta } = plan;
        const selectedRoute = routes[selectedRouteIndex];
    
        const pilotIndices = getPilotPointIndeces(selectedRoute);
        if (pilotIndices.length < 2) {
            console.log("No arrival pilot points found in route");
            return defaultResult;
        }
    
        const arrivalPointIndex = pilotIndices[pilotIndices.length-1];
        const remainingData = await this._getRemainingDataToWaypoint(selectedRoute, arrivalPointIndex, arrivalPilotEta);
    
        return {
            distanceToPilot: remainingData.distance,
            timeToPilotEta: remainingData.timeToEta,
            avgSpeedToPilotEta: remainingData.avgSpeedToEta
        };
    }

    private async updateRemainingDataToBerth() {
        const defaultResult = { distanceToBerth: null, timeToBerthEta: null, avgSpeedToBerthEta: null };

        const plan = await VesselPlanningModel?.getExecutingPlan();
        if (!plan) {
            console.log("No executing plan found");
            return defaultResult;
        }

        const { routes, selectedRouteIndex, eta } = plan;
        const selectedRoute = routes[selectedRouteIndex];

        const remainingData = await this._getRemainingDataToWaypoint(
            selectedRoute,
            selectedRoute?.route_geometry?.length ? selectedRoute.route_geometry.length - 1 : undefined,
            eta
        );

        return {
            distanceToBerth: remainingData.distance,
            timeToBerthEta: remainingData.timeToEta,
            avgSpeedToBerthEta: remainingData.avgSpeedToEta
        };
    }

    private async _getRemainingDataToWaypoint(route: VoyageRoute | undefined, waypointIndex: number | undefined, eta: number | undefined): Promise<RemainingDataToWaypoint> {
        const defaultResult = { distance: null, timeToEta: null, avgSpeedToEta: null };
        const KmToNM = 1 / 1.852;

        if (!route || !route.route_geometry || !route.route_geometry.length) {
            console.log("Route geometry not available");
            return defaultResult;
        }

        if (waypointIndex == null || waypointIndex < 0 || waypointIndex >= route.route_geometry.length) {
            console.log("Target waypoint not found in route");
            return defaultResult;
        }

        if (!VesselPositionModel!.position || !VesselPositionModel!.position.longitude || !VesselPositionModel!.position.latitude) {
            console.log("Vessel position not available");
            return defaultResult;
        }

        const position: Coordinate = [VesselPositionModel!.position.longitude, VesselPositionModel!.position.latitude];
        const nextWPIndex = await getNextWaypointIndex(route.route_geometry, true);

        if (nextWPIndex == null || nextWPIndex === -1) {
            console.log("No next waypoint found in route");
            return defaultResult;
        }

        const nextWaypoint = route.route_geometry[nextWPIndex];
        const nextWaypointCoords = nextWaypoint?.geometry.type === 'Point'
            ? nextWaypoint.geometry.coordinates as number[]
            : null;

        if (!nextWaypointCoords) {
            console.log("Next waypoint coordinates not found");
            return defaultResult;
        }

        const dists = CoordinatesUtils.calcDistance(position, nextWaypointCoords);
        const incomingLeg = route.route_geometry[nextWPIndex - 1];
        const distanceToNextWP = incomingLeg?.properties.leg_type === "GreatCircle" ? dists.gc : dists.rl;
        const distanceFromNextWpToWaypoint = getRouteDistance(route, nextWPIndex, waypointIndex) * KmToNM;
        const distance = distanceToNextWP + distanceFromNextWpToWaypoint;
        const timeToEta = eta ? new Date(eta).getTime() - Date.now() : null;
        const avgSpeedToEta = timeToEta ? (distance / timeToEta) * 3600000 : null;

        return { distance, timeToEta, avgSpeedToEta };
    }

    private async _updateExecutingPerformanceData()
    {
        const plan = await VesselPlanningModel!.getExecutingPlan();

        const delta = 1000*60*60*24*8; // 8 days
        let start = new Date(Date.now() - delta);
        let end = new Date(Date.now() + delta);
        if (plan) {
            start = new Date(plan.etd);
            end = new Date(plan.eta);
        }

        await this.syncData(start, end, plan!)
        this._currentLegData = await this.getLegData(new Date(Date.now()), "current");
        this._remainingDataToPilot = await this.updateRemainingDataToPilot();
        this._remainingDataToBerth = await this.updateRemainingDataToBerth();
        this.dispatchEvent(new Event('executing-performance-data-updated'));
    }
}
